import { gql, useMutation } from '@apollo/client'
import { InitialEditorStateType } from '@lexical/react/LexicalComposer'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import useBrands from 'pared/layouts/hooks/useBrands'

import { eitorStateValidate, isEditorState } from './useAddGlGroup'
import { IQueryDataType } from './useListGlCodesAndGroups'

export interface IOptionType {
  manageType: 'editGlGroup'
  id: string
  pAndLName: string
  formula?: string
  subRows: IOptionType[]
  isFormulaGroup?: boolean
}

export interface IDataType {
  glGroupName: string
  value: { name: string; formula: InitialEditorStateType }
  onNameChange: (value: NonNullable<IDataType['value']>['name']) => void
  onFormulaChange: (value: NonNullable<IDataType['value']>['formula']) => void
  loading: boolean
  errorMessage: { name?: string; formula?: string }
  submit: () => Promise<void>
  deletable: boolean
  delete: () => Promise<void>
}

const updateMutate = gql`
  mutation UpdateGlGroup(
    $id: BigInt!
    $name: String!
    $now: Datetime!
    $formula: JSON
  ) {
    updateGlGroup(
      input: {
        id: $id
        patch: { displayName: $name, updatedAt: $now, formula: $formula }
      }
    ) {
      clientMutationId
    }
  }
`

const deleteMutate = gql`
  mutation DeleteGlGroup($id: BigInt!) {
    deleteGlGroup(input: { id: $id }) {
      clientMutationId
    }
  }
`

const useEditGlGroup = (
  data: IQueryDataType | null,
  option: IOptionType | null,
  close: () => void,
) => {
  const editGlGroupHook = useState<IDataType['value']>({
    name: '',
    formula: null,
  })
  const isSubmittingHook = useState(false)
  const prevManageType = usePrevious(option?.manageType)
  const { brand } = useBrands()
  const [updateGlGroup, { loading: updateLoading }] = useMutation(
    updateMutate,
    {
      refetchQueries: ['ListGlCodesAndGroups'],
      awaitRefetchQueries: true,
    },
  )
  const [deleteGlGroup, { loading: deleteLoading }] = useMutation(
    deleteMutate,
    {
      refetchQueries: ['ListGlCodesAndGroups'],
      awaitRefetchQueries: true,
    },
  )
  const loading = updateLoading || deleteLoading
  const options = useMemo(
    () =>
      !option
        ? []
        : (data?.listGlGroups.nodes || []).reduce(
            (result, n) =>
              result.some((r) => r === n.displayName)
                ? result
                : [...result, n.displayName],
            [] as string[],
          ),
    [data, option],
  )

  useEffect(() => {
    if (prevManageType !== option?.manageType) {
      if (option?.manageType === 'editGlGroup')
        editGlGroupHook[1]({
          name: option.pAndLName,
          formula: option.formula || null,
        })
      else {
        editGlGroupHook[1]({
          name: '',
          formula: null,
        })
        isSubmittingHook[1](false)
      }
    }
  }, [option, prevManageType, editGlGroupHook, isSubmittingHook])

  return useMemo((): IDataType | undefined => {
    if (option?.manageType !== 'editGlGroup') return

    const [value, onChange] = editGlGroupHook
    const [isSubmitting, setIsSubmitting] = isSubmittingHook
    const validator = (isSubmitting: boolean) => {
      const name = (() => {
        if (isSubmitting) {
          if (!value.name) return `Couldn't be empty`

          if (!option.isFormulaGroup && value.name === option.pAndLName)
            return `Couldn't use the original name`
        }

        if (value.name !== option.pAndLName && options.includes(value.name))
          return `${value.name} exists`
      })()
      const formula = !option.isFormulaGroup
        ? undefined
        : eitorStateValidate(value.formula, isSubmitting)

      return { name, formula }
    }

    return {
      glGroupName: option.pAndLName,
      value,
      onNameChange: (name: string) => onChange({ ...value, name }),
      onFormulaChange: (formula: InitialEditorStateType) =>
        onChange({ ...value, formula }),
      loading,
      errorMessage: validator(isSubmitting),
      submit: async () => {
        const errorMessage = validator(true)
        const hasError = Boolean(errorMessage.name || errorMessage.formula)

        if (!isSubmitting) setIsSubmitting(true)

        if (loading || hasError) return

        await updateGlGroup({
          variables: {
            id: option.id.split('-')[1],
            name: value.name,
            formula: isEditorState(value.formula)
              ? value.formula.toJSON()
              : null,
            now: moment.utc().format(),
          },
        })
        setIsSubmitting(false)
        close()
      },
      deletable: !option.subRows?.length,
      delete: async () => {
        await deleteGlGroup({
          variables: {
            id: option.id.split('-')[1],
          },
        })
        close()
      },
    }
  }, [
    option,
    close,
    editGlGroupHook,
    isSubmittingHook,
    loading,
    brand,
    options,
  ])
}

export default useEditGlGroup
